import React, { useCallback } from "react";
import { useSelector } from "react-redux";
import { ReduxControls, Icon } from 'rhombus-react';

import { Field, getFormSyncWarnings } from "redux-form";
import PropTypes from "prop-types";
import classNames from "classnames";

import { noop } from 'utils/common';
import { SOCIAL_WIZARD } from "widgets/forms/list";
import { normalizeOptionToString } from "social/utils";
import {
	FACEBOOK_DISCLAIMER_IDS,
	FACEBOOK_MAX_DISCLAIMER_TEXT_LENGTH,
	FACEBOOK_MAX_DISCLAIMER_URL_LENGTH
} from "social/constants/common";

import GroupHeader from "widgets/GroupHeader";

import "./FacebookDisclaimer.css";

const DISCLAIMER_TEXT_PLACEHOLDER = "Enter disclaimer text";

const disclaimerIdOptions = Object.entries(FACEBOOK_DISCLAIMER_IDS).map(([key, value]) => ({
	label: value,
	value: key,
}));

const mapFbDisclaimerValueToOption = (value) => {
	const label = FACEBOOK_DISCLAIMER_IDS[value];
	return label ? {
		value,
		label,
	} : null;
};

function validDisclaimerContentSelector(adType){
	return (state) => {
		const errors = getFormSyncWarnings(SOCIAL_WIZARD)(state)?.[adType];
		const {
			disclaimerText,
			disclaimerUrl,
			"disclaimer.text" : apiDisclaimerText,
			"disclaimer.url" : apiDisclaimerUrl,
		} = errors || {};

		return [
			disclaimerText,
			disclaimerUrl,
			apiDisclaimerText,
			apiDisclaimerUrl
		].every(error => !error);
	};
}

export default function FacebookDisclaimer(props){
	const {
		input: { value, onChange } = {},
		disabled = false,
		className = "",
		adType,
		showPristineErrors,
	} = props;

	const onToggleChange = useCallback((e) => {
		e.stopPropagation();
		if(!disabled){
			onChange(!value);
		}
	}, [disabled, onChange, value]);

	const isDisclaimerContentValid = useSelector(validDisclaimerContentSelector(adType));

	return (
		<div
			className={classNames("facebook-disclaimer-wrapper", "e-form__control-wrapper", {
				[className]: !!className,
			})}
		>
			<div className="full-width e-form__control-header">
				<div
					className={classNames({
						"facebook-disclaimer-field": true,
						"is-checked": !!value,
					})}
					onClick={onToggleChange}
					disabled={disabled}
				>
					<div className="facebook-disclaimer-slider"> </div>
				</div>
				<div className="e-form__control-label">Disclaimer</div>
			</div>
			{!!value && (
				<div className="full-width">
					<div className="facebook-disclaimer-body">
						<Field
							name="disclaimerId"
							key="disclaimerId"
							label="Disclaimer Type"
							required
							component={ReduxControls.Select}
							options={disclaimerIdOptions}
							isClearable={false}
							disabled={disabled}
							normalize={normalizeOptionToString}
							format={mapFbDisclaimerValueToOption}
							onMenuScrollToBottom={noop}
							showPristineErrors={!!showPristineErrors}
						/>

						<div className="facebook-disclaimer-content">
							<GroupHeader
								required
								isValid = {isDisclaimerContentValid}
							>
								Disclaimer Content
							</GroupHeader>
							<div className="disclaimer-content-fields">
								<div className="notice">
									<Icon name="infoC" /><strong>Note: </strong> You must provide Disclaimer Text, a Disclaimer URL, or both.
								</div>
								<Field
									name="disclaimerText"
									key="disclaimerText"
									label="Disclaimer Text"
									className="facebook-disclaimer-textarea"
									component={ReduxControls.Input}
									type="textarea"
									placeholder={DISCLAIMER_TEXT_PLACEHOLDER}
									disabled={disabled}
									charMax={FACEBOOK_MAX_DISCLAIMER_TEXT_LENGTH}
									showCharCount
									showPristineErrors={!!showPristineErrors}
								/>
								<Field
									name="disclaimerUrl"
									key="disclaimerUrl"
									label="Disclaimer URL"
									component={ReduxControls.Input}
									placeholder="https://"
									disabled={disabled}
									charMax={FACEBOOK_MAX_DISCLAIMER_URL_LENGTH}
									showCharCount
									showPristineErrors={!!showPristineErrors}
								/>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

FacebookDisclaimer.displayName = "FacebookDisclaimer";

FacebookDisclaimer.propTypes = {
	adType: PropTypes.string,
	input: PropTypes.shape({
		value: PropTypes.any,
		onChange: PropTypes.func,
	}),
	disabled: PropTypes.bool,
	className: PropTypes.string,
	showPristineErrors: PropTypes.bool,
};
